import React, { useEffect, useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiUrl } from '../../utils/api';
import useInlineFillHeight from '../../hooks/useInlineFillHeight';
import './NewTestCaseModal.css';

const PRIORITY_OPTIONS = ['0', '1', '2', '3', '4'];
const mapNumericPriorityToApi = (value) => {
  const n = Number(value);
  if (n <= 0) return 'highest';
  if (n === 1) return 'high';
  if (n === 2) return 'medium';
  if (n === 3) return 'low';
  return 'lowest';
};
const mapApiPriorityToNumeric = (value) => {
  const priority = String(value || '').toLowerCase();
  if (priority === 'highest' || priority === 'critical') return '0';
  if (priority === 'high') return '1';
  if (priority === 'low') return '3';
  if (priority === 'lowest') return '4';
  return '2';
};

/**
 * Modal de edição de Caso de Teste
 * Campos: Título, Descrição, Pré-condição, Steps (com template BDD/Passo a Passo)
 */
const EditTestCaseModal = ({ isOpen, onClose, onSuccess, testCase, readOnly = false }) => {
  const { showError, showSuccess } = useNotification();
  const [inlineRef, inlineHeight] = useInlineFillHeight(isOpen);

  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'history'
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    preconditions: '',
    steps: '',
    priority: '2',
  });
  const [stepsMode, setStepsMode] = useState('bdd'); // 'bdd' | 'passo-a-passo'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);


  const actionTypeLabels = {
    created: 'Criação',
    deleted: 'Exclusão',
    field_updated: 'Campo atualizado',
    step_added: 'Passo incluído',
    step_updated: 'Passo editado',
    step_deleted: 'Passo excluído',
    // Outros tipos herdados (se futuro integrar com execuções)
    updated: 'Atualização',
    test_updated: 'Resultado de teste',
    responsible_changed: 'Responsável alterado',
    started: 'Execução iniciada',
    completed: 'Execução finalizada'
  };

  const getActionLabel = (type) => actionTypeLabels[type] || 'Alteração';

  useEffect(() => {
    if (isOpen && testCase) {
      setActiveTab('details');
      setFormData({
        title: testCase.title || '',
        description: testCase.description || '',
        preconditions: testCase.preconditions || '',
        steps: testCase.steps || '',
        priority: mapApiPriorityToNumeric(testCase.priority)
      });
      setStepsMode('bdd');
      // Histórico será carregado sob demanda ao mudar de aba
      setHistory([]); 
    }
  }, [isOpen, testCase]);

  // Carregar histórico apenas quando a aba for selecionada
  useEffect(() => {
    if (isOpen && testCase && activeTab === 'history') {
       loadCaseHistory(testCase);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, isOpen, testCase]);

  const handleClose = () => {
    if (loading) return;
    onClose && onClose();
  };

  const applyStepsTemplate = (mode) => {
    if (readOnly) return;
    setStepsMode(mode);
    if (mode === 'bdd') {
      setFormData(prev => ({ ...prev, steps: 'Feature: \n  Scenario: \n    Given \n    When \n    Then \n' }));
    } else if (mode === 'passo-a-passo') {
      setFormData(prev => ({ ...prev, steps: '- Passo 1: \n- Passo 2: \n- Passo 3: \n' }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e && e.preventDefault();
    if (readOnly) {
      showError('Você não tem permissão para editar casos.');
      return;
    }
    if (!testCase?.id) {
      showError('Caso de teste inválido.');
      return;
    }
    if (!formData.title?.trim() || !formData.description?.trim()) {
      showError('Título e descrição são obrigatórios.');
      return;
    }
    setLoading(true);
    try {
      const url = getApiUrl(`testcases/${testCase.id}`);
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          title: formData.title,
          description: formData.description,
          steps: formData.steps,
          // Mantém campos opcionais
          expected_result: testCase.expected_result || '',
          priority: mapNumericPriorityToApi(formData.priority),
          status: testCase.status || 'draft',
          project_id: testCase.project_id,
          preconditions: formData.preconditions,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        const msg = err?.error || err?.message || `Erro ao atualizar caso de teste (HTTP ${response.status})`;
        showError(msg);
        return;
      }

      const data = await response.json().catch(() => ({}));
      const updated = data?.data || data?.testCase || { ...testCase, ...formData };
      showSuccess('Caso de teste atualizado com sucesso!');
      onSuccess && onSuccess(updated);
      handleClose();
    } catch (error) {
      console.error('Erro ao atualizar caso de teste:', error);
      showError(error?.message || 'Falha na requisição ao atualizar caso de teste');
    } finally {
      setLoading(false);
    }
  };

  const loadCaseHistory = async (tc) => {
    if (!tc?.id) return;
    setHistoryLoading(true);
    try {
      const url = getApiUrl(`testcases/${tc.id}/history`);
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token') || ''}` } });
      const json = await res.json().catch(() => ({}));
      const list = Array.isArray(json?.data) ? json.data : (Array.isArray(json) ? json : []);
      setHistory(list);
    } catch (e) {
      console.error('Erro ao carregar histórico do caso:', e);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  

  if (!isOpen) return null;

  return (
    <div className="modal new-test-case-modal edit-test-case-inline" ref={inlineRef} style={inlineHeight ? { height: inlineHeight } : undefined}>
      <div className="modal-header">
        <h2>Editar Caso de Teste</h2>
      </div>

        <div className="modal-form">
          <div className="tabs">
            <button className={activeTab === 'details' ? 'tab active' : 'tab'} onClick={() => setActiveTab('details')}>Detalhes do Caso</button>
            <button className={activeTab === 'history' ? 'tab active' : 'tab'} onClick={() => setActiveTab('history')}>Histórico</button>
          </div>

          {activeTab === 'details' && (
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-title">Nome do Caso de Teste *</label>
                  <input
                    id="edit-title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    disabled={readOnly}
                    placeholder="Digite o nome do caso de teste"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-description">Descrição</label>
                  <textarea
                    id="edit-description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    disabled={readOnly}
                    placeholder="Descreva o objetivo do caso de teste"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-preconditions">Pré Condição</label>
                  <textarea
                    id="edit-preconditions"
                    value={formData.preconditions}
                    onChange={(e) => handleChange('preconditions', e.target.value)}
                    disabled={readOnly}
                    placeholder="Descreva as condições necessárias antes da execução"
                    rows="2"
                  />
                </div>
              </div>

              <div className="form-section">
                <div className="section-header">
                  <div className="steps-mode-control">
                    <label style={{ marginRight: 8 }}>Add Steps</label>
                    <select value={stepsMode} onChange={(e) => applyStepsTemplate(e.target.value)} disabled={readOnly}>
                      <option value="bdd">BDD</option>
                      <option value="passo-a-passo">Passo a Passo</option>
                    </select>
                  </div>
                  <div className="priority-control-row">
                    <label htmlFor="edit-priority">Prioridade</label>
                    <select
                      id="edit-priority"
                      value={formData.priority}
                      onChange={(e) => handleChange('priority', e.target.value)}
                      disabled={readOnly}
                    >
                      {PRIORITY_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="step-group">
                  <div className="step-header">{stepsMode === 'passo-a-passo' ? 'Passo a Passo' : 'Gherkin'}</div>
                  <div className="step-content">
                    <div className="form-group">
                      <textarea
                        value={formData.steps}
                        onChange={(e) => handleChange('steps', e.target.value)}
                        disabled={readOnly}
                        placeholder={stepsMode === 'bdd' ? 'Given/When/Then...' : 'Liste os passos...'}
                        style={{ minHeight: 160 }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-danger" onClick={handleClose} disabled={loading}>Cancelar</button>
                {(!readOnly) && (
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    {loading ? 'Salvando...' : 'Salvar Caso'}
                  </button>
                )}
              </div>
            </form>
          )}

          {activeTab === 'history' && (
            <div>
              {historyLoading ? (
                <div className="muted">Carregando histórico...</div>
              ) : (
                <ul className="list" style={{ maxHeight: 360, overflowY: 'auto' }}>
                  {history.map(h => (
                    <li key={h.id || `${h.action_type}-${h.created_at}`} className="list-item">
                      <span>
                        <strong>{getActionLabel(h.action_type)}</strong> — {h.description || 'Alteração registrada'}
                      </span>
                      <span className="muted" style={{ marginLeft: 8 }}>
                        {new Date(h.created_at || Date.now()).toLocaleString()}
                      </span>
                      {h.user_name && (
                        <span className="muted" style={{ marginLeft: 8 }}>
                          por {h.user_name}
                        </span>
                      )}
                    </li>
                  ))}
                  {history.length === 0 && (
                    <li className="muted">Nenhum histórico disponível para este caso</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>
  );
};

export default EditTestCaseModal;
